import type { FC } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

// libraries
import { Grid } from '@interstate/components/Grid';
import { Box } from '@interstate/components/Box';
import { Typography } from '@interstate/components/Typography';
import { TextInput } from '@interstate/components/TextInput';
import { Tooltip } from '@interstate/components/Tooltip';

// components
import { FormPriceInput } from '../formManager/FormPriceInput';
import { FormSelectInput } from '../formManager/FormSelectInput';

// consts/enums
import { WHOLESALE_BOOK_SOURCE_OPTIONS } from '../../constants';
import { BOOK_SOURCE_TO_TYPE_OPTIONS } from '../constants';
import { AccelerateVehicleCondition, FINANCE, LEASE, type BookType } from '@makemydeal/dr-platform-types';

// utils
import { bookOutValueValidator, pricingInputList } from '../formManager/helper';
import { FMField } from '../types';

// selectors
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

// context
import { FormContext } from '../formManager/formContext';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { CviCostMismatchAlert, type CviPricingUpdate } from '../../common/CviCostMismatchAlert';

const PricingForm: FC = () => {
    const vehicleCondition = useSelector(vehicleSelectors.getVehicle).condition;
    const isVehicleConditionNew = vehicleCondition === AccelerateVehicleCondition.NEW;

    // Form context for real-time values
    const { form, changeHandler } = useContext(FormContext);

    // Stage CVI cost and invoice changes in form local state (applied on Apply click)
    const handleCviPricingUpdate = useCallback(
        ({ cost, invoiceAmount }: CviPricingUpdate) => {
            if (cost != null) {
                changeHandler('cost', cost);
            }
            if (invoiceAmount != null) {
                changeHandler('invoiceAmount', invoiceAmount);
            }
        },
        [changeHandler]
    );

    // Get current offer type and deal values from Redux
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const amountFinanced = useSelector((state) => {
        const selectedTerm = offerReduxSelectors.getSelectedTerm(state);
        return selectedTerm?.amountFinanced;
    });
    const adjustedCapCost = useSelector(offerReduxSelectors.getAdjustedCapCost);
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
    const isDealerPreferredBookSourceEnabled = useSelector(featureToggleSelectors.isEnableDealerPreferredBookSource);
    const isQuoteWithoutVehicle = useSelector(vehicleSelectors.getIsQuoteWithoutVehicle);
    const showBookoutFields = !isQuoteWithoutVehicle;

    const bookoutValue = form.values.wholesaleValue as number;
    const bookoutTypeOptions = BOOK_SOURCE_TO_TYPE_OPTIONS[form.values.wholesaleBookSource as BookType];

    // Calculate LTV in real-time based on form values
    const { shouldShowLtvField, formattedLtv } = useMemo(() => {
        // Cannot calculate if bookout value is missing or zero
        if (!bookoutValue || bookoutValue <= 0) {
            return { shouldShowLtvField: false, formattedLtv: undefined };
        }

        let ltvValue: number | undefined;
        let shouldShow = false;

        if (currentOfferType === FINANCE && !isFlexBuyPayment) {
            // Finance: LTV = 100 * Amount Financed / Bookout Value
            // Note: Do not show LTV for Flex Buy deals
            if (amountFinanced != null && amountFinanced >= 0) {
                ltvValue = (100 * amountFinanced) / bookoutValue;
                shouldShow = true;
            }
        } else if (currentOfferType === LEASE) {
            // Lease: LTV = 100 * Adjusted Cap Cost / Bookout Value (only if > 0)
            if (adjustedCapCost != null && adjustedCapCost > 0) {
                ltvValue = (100 * adjustedCapCost) / bookoutValue;
                shouldShow = true;
            }
        }

        const formatted = ltvValue != null ? `${ltvValue.toFixed(1)}%` : undefined;
        return { shouldShowLtvField: shouldShow, formattedLtv: formatted };
    }, [currentOfferType, isFlexBuyPayment, amountFinanced, adjustedCapCost, bookoutValue]);

    return (
        <Grid container spacing={2} paddingBottom={1}>
            <Grid xs={12}>
                <Typography variant="h5">Pricing</Typography>
                <CviCostMismatchAlert onUpdate={handleCviPricingUpdate} />
            </Grid>
            {pricingInputList.map(({ name, label }) => {
                const computedLabel = name === 'msrp' ? (isVehicleConditionNew ? 'MSRP' : 'Retail Price') : label;

                return (
                    <Grid key={name} xs={6}>
                        <FormPriceInput name={name} label={computedLabel} />
                    </Grid>
                );
            })}
            {showBookoutFields && (
                <Grid key="wholesaleBookSource" xs={6}>
                    <FormSelectInput
                        name={FMField.wholesaleBookSource}
                        label="Bookout Source"
                        errorMessage={
                            isGCDynamicValidationsEnabled && form.error[FMField.wholesaleBookSource]
                                ? form.error[FMField.wholesaleBookSource]
                                : 'Required'
                        }
                        options={WHOLESALE_BOOK_SOURCE_OPTIONS}
                        maxHeight="250px"
                        placeholder="Select"
                    />
                </Grid>
            )}
            {showBookoutFields && isDealerPreferredBookSourceEnabled && bookoutTypeOptions && (
                <Grid key="wholesaleValueType" xs={6}>
                    <FormSelectInput
                        name={FMField.wholesaleValueType}
                        label="Bookout Type"
                        options={bookoutTypeOptions}
                        maxHeight="250px"
                        placeholder="Select"
                    />
                </Grid>
            )}
            {showBookoutFields && (
                <Grid key="wholesaleValue" xs={6}>
                    <div data-testid="wholesaleValue-root">
                        <FormPriceInput
                            name="wholesaleValue"
                            label="Bookout Value"
                            maxValue={999999}
                            validator={isVehicleConditionNew ? undefined : bookOutValueValidator}
                        />
                    </div>
                </Grid>
            )}
            {shouldShowLtvField && (
                <Grid key="ltv" xs={6}>
                    <Tooltip
                        toolTipContent="LTV = (Loan Amount / Vehicle Book Value) x 100%"
                        size="large"
                        position="bottom"
                        data-testid="ltv-tooltip"
                    >
                        <Box>
                            <TextInput
                                data-testid="vehicle-input-ltv"
                                label="Loan to Value (LTV)"
                                id="ltv"
                                name="ltv"
                                value={formattedLtv}
                                disabled
                            />
                        </Box>
                    </Tooltip>
                </Grid>
            )}
        </Grid>
    );
};

export default PricingForm;
